<template>
    <HeaderCardsLayout v-if="series" :logo-url="iconUrl" :logo-alt="`Logo ${series.nameShort}`" variant="Logo">
        <template #headline>{{ series.nameShort }}</template>
        <template #headline-extra>
            <BadgeDropDown
                :label="$t('event-series-status-label')"
                class="text-base font-normal"
                :current-value="getEventSeriesStatusItem(series.status as EventSeriesStatus)"
                :values="getEventSeriesStatusItems(series.status as EventSeriesStatus)"
                :status="statusDropDownStatus"
                :tooltip="changeStatusDisabledExplanation"
                size="Large-40"
                @change-value="(status: string) => updateEventSeriesStatus(status as EventSeriesStatus)"
            />
        </template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="series.nameLong">
                <template #currentIcon>
                    <SingleEventIcon v-if="series.singleEvent" class="inline-block align-sub" />
                </template>
                <BreadcrumbLink :target="{ name: 'events' }">
                    <BallotIcon class="mr-1 shrink-0" />
                    {{ $t('event-wiki') }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>

        <template v-if="series.singleEvent" #icon><SingleEventIcon class="h-12 w-12" /></template>

        <template #buttons>
            <Button
                variant="Info-Light"
                size="Base"
                :disabled-explanation="convertDisabledExplanation"
                @click="toggleSingleEvent"
            >
                <template v-if="!series.singleEvent" #icon>
                    <SingleEventIcon class="size-5" />
                </template>
                {{ convertButtonLabel }}
            </Button>
            <Button
                variant="Danger-Strong"
                size="Base"
                :disabled-explanation="deleteDisabledExplanation"
                @click="deleteEventSeries"
            >
                <template #icon>
                    <DeleteForeverIcon class="size-5" />
                </template>
                {{ $t('delete') }}
            </Button>
            <LinkButton
                variant="Info-Strong"
                size="Base"
                :disabled-explanation="createEventDisabledExplanation"
                :to="getCreateEventLink(seriesId, series.nameLong)"
            >
                <template #icon>
                    <PlusIcon class="size-5" />
                </template>
                {{ createEventButtonLabel }}
            </LinkButton>
        </template>
        <template #tab-navigation>
            <TabNavigation
                :headline="$t('tab')"
                :items="tabNavigationItems"
                :tab-panel-id="tabPanelId"
                :tabs-disabled-text="tabsDisabledText"
                @focus-tab-panel="focusTabPanel"
            />
        </template>
        <template #body>
            <PaddedContent>
                <TabPanel :id="tabPanelId" ref="tabPanelRef" :headline="activeTabHeadline">
                    <RouterView v-slot="{ Component }">
                        <component :is="Component"></component>
                    </RouterView>
                </TabPanel>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import BadgeDropDown from '@/components/BadgeDropDown/BadgeDropDown.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import BallotIcon from '@/components/Icon/BallotIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import PlusIcon from '@/components/Icon/PlusIcon.vue';
import SingleEventIcon from '@/components/Icon/SingleEventIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import TabNavigation from '@/components/TabNavigation/TabNavigation.vue';
import TabPanel from '@/components/TabNavigation/TabPanel.vue';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { nanoid } from 'nanoid';
import { computed, provide, ref } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useEventSeries } from '../composables/useEventSeries';
import { getCreateEventLink, getEventSeriesStatusItem, getEventSeriesStatusItems } from '../event';
import { eventSeriesKey } from '../keys';
import { EventSeriesStatus } from '../types';

const props = defineProps<{ seriesId: string }>();
const fluent = useFluent();

const tabPanelRef = ref();
const seriesId = computed(() => props.seriesId);
const isAdmin = auth.isAdmin();

const {
    series,
    loading,
    convertDisabledExplanation,
    createEventDisabledExplanation,
    deleteDisabledExplanation,
    createEventButtonLabel,
    convertButtonLabel,
    iconUrl,
    updateEventSeriesStatusError,
    updateEventSeriesStatusLoading,
    updateEventSeries,
    updateEventSeriesStatus,
    toggleSingleEvent,
    deleteEventSeries,
    ...rest
} = useEventSeries(seriesId);

provide(eventSeriesKey, {
    series,
    loading,
    createEventDisabledExplanation,
    convertDisabledExplanation,
    deleteDisabledExplanation,
    createEventButtonLabel,
    convertButtonLabel,
    iconUrl,
    updateEventSeriesStatusError,
    updateEventSeriesStatusLoading,
    updateEventSeries,
    updateEventSeriesStatus,
    toggleSingleEvent,
    deleteEventSeries,
    ...rest,
});

const { activeCard, getDisabledExplanation } = useActiveCard();

const changeStatusDisabledExplanation = computed(() => {
    return isAdmin ? undefined : fluent.$t('no-permission-to-edit');
});

const statusDropDownStatus = computed(() => {
    if (changeStatusDisabledExplanation.value) return 'Disabled';
    if (updateEventSeriesStatusLoading.value) return 'Loading';
    if (updateEventSeriesStatusError.value) return 'Retry';
    return 'Default';
});

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) return false;
    }
});
const tabsDisabledText = computed(() => fluent.$t('tabs-disabled', { name: activeCard.value.label }));
const tabPanelId = nanoid();

const isOverviewTabDisabled = computed(() => Boolean(getDisabledExplanation('event-series-overview').value));
const isDetailsTabDisabled = computed(() => Boolean(getDisabledExplanation('event-series-details').value));
const isOrganisationalStructureTabDisabled = computed(() => Boolean(getDisabledExplanation('event-series-org').value));
const isAppointmentTabDisabled = computed(() => Boolean(getDisabledExplanation('event-series-appointment').value));
const isLocationRequirementsTabDisabled = computed(() =>
    Boolean(getDisabledExplanation('event-series-location').value),
);
const isLocationLinksTabDisabled = computed(() => Boolean(getDisabledExplanation('event-series-links').value));

const tabNavigationItems = ref([
    {
        target: { name: 'event-series-overview' },
        routeName: 'event-series-overview',
        label: fluent.$t('overview'),
        isDisabled: isOverviewTabDisabled,
    },
    {
        target: { name: 'event-series-details' },
        routeName: 'event-series-details',
        label: fluent.$t('master-data'),
        isDisabled: isDetailsTabDisabled,
    },
    {
        target: { name: 'event-series-org' },
        routeName: 'event-series-org',
        label: fluent.$t('organisational-structure'),
        isDisabled: isOrganisationalStructureTabDisabled,
    },
    {
        target: { name: 'event-series-appointment' },
        routeName: 'event-series-appointment',
        label: fluent.$t('appointment'),
        isDisabled: isAppointmentTabDisabled,
    },
    {
        target: { name: 'event-series-location' },
        routeName: 'event-series-location',
        label: fluent.$t('location-requirements'),
        isDisabled: isLocationRequirementsTabDisabled,
    },
    {
        target: { name: 'event-series-links' },
        routeName: 'event-series-links',
        label: fluent.$t('links'),
        isDisabled: isLocationLinksTabDisabled,
    },
]);

const route = useRoute();
const activeTabHeadline = computed(() => {
    return fluent.$t('tab-panel', {
        activeTabName: tabNavigationItems.value.find((item) => item.routeName === route.name)?.label ?? '',
    });
});

function focusTabPanel() {
    tabPanelRef.value.focus();
}

useHeadSafe({ title: () => `${series.value?.nameShort ?? ''} - ${fluent.$t('event-wiki')}` });
</script>
